import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    IconButton,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Chip,
    CircularProgress,
    Card,
    CardContent,
    Grid,
    InputAdornment,
    Tabs,
    Tab,
    List,
    ListItem,
    ListItemText,
    Divider
} from '@mui/material';
import {
    Edit as EditIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    AttachMoney as MoneyIcon,
    History as HistoryIcon,
    LiveTv as LiveTvIcon,
    Settings as SettingsIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

function TvPackagePricing() {
    const { currentUser } = useAuth();
    const [loading, setLoading] = useState(true);
    const [packages, setPackages] = useState([]);
    const [priceTable, setPriceTable] = useState({});
    const [addonPricing, setAddonPricing] = useState({});
    const [editingPackage, setEditingPackage] = useState(null);
    const [editingAddon, setEditingAddon] = useState(false);
    const [editValues, setEditValues] = useState({});
    const [addonValues, setAddonValues] = useState({});
    const [saving, setSaving] = useState(false);
    const [historyDialog, setHistoryDialog] = useState(false);
    const [priceHistory, setPriceHistory] = useState([]);
    const [tabValue, setTabValue] = useState(0);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        fetchPricing();
    }, []);

    const fetchPricing = async () => {
        setLoading(true);
        try {
            const response = await axios.get('/api/tv-packages/pricing');
            setPackages(response.data.packages || []);
            setPriceTable(response.data.priceTable || {});
            setAddonPricing(response.data.addonPricing || {});
            setAddonValues(response.data.addonPricing || {});
        } catch (error) {
            console.error('Error fetching pricing:', error);
            setError('Failed to load pricing data');
        }
        setLoading(false);
    };

    const fetchHistory = async () => {
        try {
            const response = await axios.get('/api/tv-packages/pricing-history');
            setPriceHistory(response.data.history || []);
            setHistoryDialog(true);
        } catch (error) {
            console.error('Error fetching history:', error);
            setError('Failed to load pricing history');
        }
    };

    const handleEditPackage = (pkg) => {
        setEditingPackage(pkg.package_key);
        setEditValues({ ...editValues, [pkg.package_key]: pkg.price });
    };

    const handleSavePackage = async (packageKey) => {
        const newPrice = parseFloat(editValues[packageKey]);
        
        if (isNaN(newPrice) || newPrice < 0) {
            setError('Please enter a valid price');
            return;
        }

        setSaving(true);
        try {
            await axios.put(`/api/tv-packages/pricing/${packageKey}`, { price: newPrice });
            setSuccess('Package price updated successfully');
            setEditingPackage(null);
            fetchPricing(); // Refresh data
            
            // Clear success message after 3 seconds
            setTimeout(() => setSuccess(''), 3000);
        } catch (error) {
            console.error('Error updating price:', error);
            setError('Failed to update price');
        }
        setSaving(false);
    };

    const handleSaveAddon = async (addonType) => {
        const newPrice = parseFloat(addonValues[addonType]);
        
        if (isNaN(newPrice) || newPrice < 0) {
            setError('Please enter a valid price');
            return;
        }

        setSaving(true);
        try {
            await axios.put('/api/tv-packages/addon-pricing', { 
                addonType, 
                price: newPrice 
            });
            setSuccess('Add-on price updated successfully');
            setEditingAddon(false);
            fetchPricing(); // Refresh data
            
            // Clear success message after 3 seconds
            setTimeout(() => setSuccess(''), 3000);
        } catch (error) {
            console.error('Error updating addon price:', error);
            setError('Failed to update add-on price');
        }
        setSaving(false);
    };

    const formatPrice = (price) => {
        return `$${parseFloat(price).toFixed(2)}`;
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <LiveTvIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
                TV Package Pricing Management
            </Typography>

            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            <Paper sx={{ mb: 3 }}>
                <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                    <Tab label="Package Pricing" icon={<MoneyIcon />} iconPosition="start" />
                    <Tab label="Add-on Pricing" icon={<SettingsIcon />} iconPosition="start" />
                </Tabs>
            </Paper>

            {tabValue === 0 && (
                <>
                    {/* Package Pricing Table */}
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Package Name</TableCell>
                                    <TableCell align="center">Theme Packs</TableCell>
                                    <TableCell align="center">Premium Channels</TableCell>
                                    <TableCell align="right">Current Price</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {packages.map((pkg) => (
                                    <TableRow key={pkg.package_key}>
                                        <TableCell>
                                            <Typography variant="body1" fontWeight="medium">
                                                {pkg.package_name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {pkg.package_key}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            {pkg.theme_count > 0 ? (
                                                <Chip 
                                                    label={pkg.theme_count} 
                                                    size="small" 
                                                    color="primary"
                                                />
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell align="center">
                                            {pkg.premium_count > 0 ? (
                                                <Chip 
                                                    label={pkg.premium_count} 
                                                    size="small" 
                                                    color="secondary"
                                                />
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell align="right">
                                            {editingPackage === pkg.package_key ? (
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    value={editValues[pkg.package_key] || ''}
                                                    onChange={(e) => setEditValues({
                                                        ...editValues,
                                                        [pkg.package_key]: e.target.value
                                                    })}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">$</InputAdornment>
                                                        ),
                                                    }}
                                                    sx={{ width: 120 }}
                                                />
                                            ) : (
                                                <Typography variant="h6" color="primary">
                                                    {formatPrice(pkg.price)}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            {editingPackage === pkg.package_key ? (
                                                <>
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => handleSavePackage(pkg.package_key)}
                                                        disabled={saving}
                                                    >
                                                        <SaveIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        color="default"
                                                        onClick={() => setEditingPackage(null)}
                                                        disabled={saving}
                                                    >
                                                        <CancelIcon />
                                                    </IconButton>
                                                </>
                                            ) : (
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => handleEditPackage(pkg)}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="outlined"
                            startIcon={<HistoryIcon />}
                            onClick={fetchHistory}
                        >
                            View Price History
                        </Button>
                    </Box>
                </>
            )}

            {tabValue === 1 && (
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Theme Pack Add-on Pricing
                                </Typography>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Price per additional theme pack beyond package limit
                                </Typography>
                                
                                <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                                    {editingAddon ? (
                                        <>
                                            <TextField
                                                label="Price per Theme Pack"
                                                type="number"
                                                value={addonValues.theme_pack || ''}
                                                onChange={(e) => setAddonValues({
                                                    ...addonValues,
                                                    theme_pack: e.target.value
                                                })}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">$</InputAdornment>
                                                    ),
                                                }}
                                                fullWidth
                                            />
                                            <IconButton
                                                color="primary"
                                                onClick={() => handleSaveAddon('theme_pack')}
                                                disabled={saving}
                                            >
                                                <SaveIcon />
                                            </IconButton>
                                            <IconButton
                                                color="default"
                                                onClick={() => {
                                                    setEditingAddon(false);
                                                    setAddonValues(addonPricing);
                                                }}
                                                disabled={saving}
                                            >
                                                <CancelIcon />
                                            </IconButton>
                                        </>
                                    ) : (
                                        <>
                                            <Typography variant="h4" color="primary">
                                                {formatPrice(addonPricing.theme_pack || 10)}
                                            </Typography>
                                            <Typography variant="body1">
                                                per theme pack
                                            </Typography>
                                            <IconButton
                                                color="primary"
                                                onClick={() => setEditingAddon(true)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    How Add-on Pricing Works
                                </Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemText
                                            primary="Customer selects channels"
                                            secondary="System determines required theme packs"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemText
                                            primary="Find best base package"
                                            secondary="Package with closest match to requirements"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemText
                                            primary="Calculate add-ons"
                                            secondary="Additional theme packs × add-on price"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemText
                                            primary="Total price"
                                            secondary="Base package + add-on charges"
                                        />
                                    </ListItem>
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <Alert severity="info">
                            <Typography variant="body2">
                                <strong>Note:</strong> The system automatically calculates the best value for customers by comparing 
                                all package combinations including add-ons. Sometimes a higher package with no add-ons 
                                is cheaper than a lower package with multiple add-ons.
                            </Typography>
                        </Alert>
                    </Grid>
                </Grid>
            )}

            {/* Price History Dialog */}
            <Dialog
                open={historyDialog}
                onClose={() => setHistoryDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Typography variant="h6">
                        Price Change History
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <List>
                        {priceHistory.map((record, index) => (
                            <React.Fragment key={record.id}>
                                <ListItem>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body1">
                                                    {record.package_key}
                                                </Typography>
                                                <Chip
                                                    label={`${formatPrice(record.old_price)} → ${formatPrice(record.new_price)}`}
                                                    size="small"
                                                    color="primary"
                                                />
                                            </Box>
                                        }
                                        secondary={
                                            <Box>
                                                <Typography variant="caption">
                                                    Changed by: {record.changed_by_name || 'Unknown'}
                                                </Typography>
                                                <br />
                                                <Typography variant="caption">
                                                    Date: {formatDate(record.changed_at)}
                                                </Typography>
                                                {record.change_reason && (
                                                    <>
                                                        <br />
                                                        <Typography variant="caption">
                                                            Reason: {record.change_reason}
                                                        </Typography>
                                                    </>
                                                )}
                                            </Box>
                                        }
                                    />
                                </ListItem>
                                {index < priceHistory.length - 1 && <Divider />}
                            </React.Fragment>
                        ))}
                        {priceHistory.length === 0 && (
                            <ListItem>
                                <ListItemText
                                    primary="No price changes recorded"
                                    secondary="Price history will appear here when changes are made"
                                />
                            </ListItem>
                        )}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHistoryDialog(false)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default TvPackagePricing;